'use client';

import { useState, useEffect } from 'react';
import { APP_CONFIG } from '@/config/app';

interface Branding {
    name: string;
    tagline: string;
    description: string;
    logoUrl: string | null;
    logoInlineUrl: string | null;
    faviconUrl: string | null;
}

const DEFAULT_BRANDING: Branding = {
    name: APP_CONFIG.name,
    tagline: APP_CONFIG.tagline,
    description: APP_CONFIG.description,
    logoUrl: null,
    logoInlineUrl: null,
    faviconUrl: null,
};

// Module-level cache so multiple components share the same data
let cachedBranding: Branding | null = null;
let fetchPromise: Promise<Branding> | null = null;

async function fetchBranding(): Promise<Branding> {
    try {
        const res = await fetch('/api/branding');
        if (!res.ok) return DEFAULT_BRANDING;
        return await res.json();
    } catch {
        return DEFAULT_BRANDING;
    }
}

/**
 * Hook to get platform branding (name, tagline, logo).
 * Fetches once and caches at module level — shared across all components.
 */
export function useBranding(): Branding {
    const [branding, setBranding] = useState<Branding>(cachedBranding || DEFAULT_BRANDING);

    useEffect(() => {
        if (cachedBranding) {
            setBranding(cachedBranding);
            return;
        }

        if (!fetchPromise) {
            fetchPromise = fetchBranding();
        }

        fetchPromise.then(data => {
            cachedBranding = data;
            setBranding(data);
        });
    }, []);

    return branding;
}
